import React, { useRef, useState, useCallback, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faFloppyDisk, faXmark, faRuler, faPencil, faEye, faLock, faCheck } from "@fortawesome/pro-solid-svg-icons";
import { produce } from "immer";
import { toast } from "react-toastify";
import useProjectEditAccess from "@/services/context/project-process/use-project-edit-access";

import useProjectContext from "@/services/context/project/use-project-context.ts";
import useProjectOrganisationContext from "@/pages/takeoff/context/project-organisation/use-project-organisation-context.ts";
import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context.ts";

import FileData2D from "@/pages/takeoff/context/project-organisation/models/file-data-2d.ts";
import SetupFileStoreyData from "@/pages/takeoff/context/project-organisation/models/setup-file-data.ts";
import FileType from "@/models/enums/file-type.ts";
import ProjectLockStatus from "@/models/enums/project-lock-status.ts";

import TruncatedText from "@/components/text/truncated-text";
import Card from "@/components/card";
import Button from "@/components/buttons/button";
import ProjectEditButton from "@/components/buttons/project-edit-button";
import { applySetupFiles } from "@/pages/takeoff/context/project-organisation/project-organisation-helper";
import ProjectOrganisation from "@/pages/takeoff/context/project-organisation/models/project-organisation";
import useProjectFormNavigationContext
    from "@/pages/takeoff/context/project-form-navigation/use-project-form-navigation-context.ts";
import LoadingButtonLockDependant from "@/components/buttons/loading-button-lock-dependant";
import scaleHighlight from "../styles/scale-highlight.module.css";

interface MeasureCard2DProps {
    onRegisterFileSelect?: (handler: (files: FileList | null) => void) => void;
}

const MeasureCard2D: React.FC<MeasureCard2DProps> = ({ onRegisterFileSelect }) => {
    const { ensureCanEdit, canEdit } = useProjectEditAccess();
    const { setRoute } = useProjectFormNavigationContext();
    
    const { project, projectLockStatus } = useProjectContext();
    const { data, isSaving, setupFiles, saveChanges } = useProjectOrganisationContext();
    const { completeRefreshData } = useProjectMeasureContext();

    const [itemsSource, setItemsSource] = useState<FileData2D[]>([]);
    const [newItems, setNewItems] = useState<SetupFileStoreyData[]>([]);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (data == null) return;

        const files: FileData2D[] = [];
        let fileData = data.files2d;
        if (fileData != null) {
            files.push(...fileData);
        }
        data.buildings.forEach((b) => {
            fileData = b.files2d;
            if (fileData != null) {
                files.push(...fileData);
            }

            b.storeys.forEach((s) => {
                fileData = s.files2d;
                if (fileData != null) {
                    files.push(...fileData);
                }
            });
        });

        setItemsSource(files);
    }, [data]);

    const handleFilesSaving = useCallback(async (
        projectData: ProjectOrganisation
    ) => saveChanges(projectData)
            .then(() => completeRefreshData())
            .catch((error) => {
                if (error instanceof Error) {
                    toast.error(error.message, { autoClose: false });
                }
            })
    , [saveChanges, completeRefreshData]);

    const handleDelete = useCallback((item: FileData2D) => {
        if (!ensureCanEdit()) {
            return;
        }

        setItemsSource(itemsSource.filter(i => i.id !== item.id));
        setNewItems(newItems.filter(i => i.fileData.id !== item.id));
    }, [ensureCanEdit, itemsSource, newItems, setItemsSource, setNewItems]);

    const handleFileSelect = useCallback(async (files: FileList | null) => {
        if (!ensureCanEdit()) {
            return;
        }

        if (!files || files.length === 0) return;

        setUploadError(null);
        const firstFile = files[0];

        if (!firstFile.name.toLowerCase().endsWith('.pdf')) {
            setUploadError("Seuls les fichiers PDF sont acceptés");
            return;
        }

        try {
            Array.from(files).forEach((file) => {
                if (!file.name.toLowerCase().endsWith('.pdf')) return;
                const fileData2D = new FileData2D(crypto.randomUUID(), file.name, FileType.File2D, 0, '', '');
                const itemData: SetupFileStoreyData =
                {
                    file, fileData: fileData2D,
                }
                setNewItems((prev) => [...prev, itemData]);
                setItemsSource((prev) => [...prev, fileData2D]);
            });
        } catch (error) {
            if (error instanceof Error)
                setUploadError(error.message);
        }

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [ensureCanEdit, setNewItems, setItemsSource]);

    useEffect(() => {
        onRegisterFileSelect?.(handleFileSelect);
    }, [onRegisterFileSelect, handleFileSelect]);

    const ItemTemplate = function (item: FileData2D) {
        if (data == null)
            return <></>;

        const hasScale = item.content?.default === false;
        const fileData = data.findBuildingStoreyAndFileFromFileId(item.id);

        return (
            <div className='flex flex-row gap-x-2 items-center w-full p-1 text-sm text-gray-900' key={item.id}>
                <TruncatedText text={item.name} className='min-w-0 flex-1' />
                {newItems.find(i => i.fileData.id === item.id) != null ? (
                    <Button variant='simple' size='xs' onClick={() => handleDelete(item)}>
                        <FontAwesomeIcon icon={faXmark} className='text-red-500' />
                    </Button>
                ) : (
                    <>
                        {projectLockStatus === ProjectLockStatus.LOCK_OWNED ? (
                            <>
                                <Button variant='simple' size='xs' className={`${scaleHighlight.target} cursor-pointer`} disabled={isSaving}
                                    onClick={() => {
                                        setRoute('organisation', new URLSearchParams({fileId: item.id}), 'measures');
                                    }}>
                                    <FontAwesomeIcon icon={faRuler} />
                                    {hasScale &&
                                        <FontAwesomeIcon icon={faCheck} className={'text-green-500'}/>
                                    }
                                </Button>
                            </>
                        ) : (
                            <div className='flex flex-row gap-x-2'>
                                <ProjectEditButton variant='simple' size='xs' disabled={!fileData}>
                                    <div className='flex flex-row gap-x-2 items-center'>
                                        <FontAwesomeIcon icon={faLock} className='text-red-500' />
                                        <FontAwesomeIcon icon={faRuler} />
                                        <FontAwesomeIcon icon={faPencil} />
                                    </div>
                                </ProjectEditButton>
                            </div>
                        )}

                        <Button size='xs' variant='simple' className='cursor-pointer'
                                disabled={!hasScale || !fileData}
                                onClick={() => setRoute('measures', new URLSearchParams({ storeyId: fileData!.storey.id }))}>
                            <FontAwesomeIcon icon={projectLockStatus === ProjectLockStatus.LOCK_OWNED ? faPencil : faEye} fixedWidth={true} />
                        </Button>
                    </>
                )}
            </div>
        )
    }

    return (
        <div className='flex flex-col gap-y-2 overflow-y-auto pointer-events-auto'>
            <Card additionalClasses='max-w-80'>
                <div className='flex flex-col w-full min-h-30 relative'>
                    <input disabled={!canEdit}
                        ref={fileInputRef}
                        type="file"
                        accept='.pdf'
                        className="hidden"
                        onChange={(e) => handleFileSelect(e.target.files)}
                    />
                    <ProjectEditButton
                        variant='ghost' className='ml-auto mr-1 my-1'
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <FontAwesomeIcon icon={faPlus} className="text-green-500" />
                    </ProjectEditButton>

                    <div className='border-t border-gray-300 p-1'>
                        {itemsSource.map(item => ItemTemplate(item))}
                    </div>

                    <div className="justify-center flex flex-col">
                        {uploadError && (
                            <div className="text-red-500 text-xs text-center mt-1">{uploadError}</div>
                        )}
                    </div>
                </div>
            </Card>
            
            <LoadingButtonLockDependant 
                className='ml-auto' disabled={data?.isDirty === false && newItems.length === 0} 
                isLoading={isSaving} isLoadingMessage='Sauvegarde en cours...'
                onClick={() => {
                    if (!data) return;

                    setupFiles(newItems);

                    const updatedProject = produce(data, draft => {
                        applySetupFiles(
                            draft,
                            newItems,
                            project.flowMode
                        );
                    });

                    handleFilesSaving(updatedProject);
                    setNewItems([]);
                }}>
                <div className='flex flex-row gap-x-2 items-center'>
                    <FontAwesomeIcon icon={faFloppyDisk} className='text-white' />
                    <span>Sauvegarder</span>
                </div>
            </LoadingButtonLockDependant>
        </div>
    );
};

export default MeasureCard2D;